import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatPrice, stockStatus } from "@/lib/api";
import { useContent } from "@/lib/content";
import useLenis from "@/hooks/useLenis";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { IMG_FALLBACK } from "@/components/Collections";

const VALID = ["originals", "faucet", "skate", "goods"];
const onImgError = (e) => {
  if (e.currentTarget.dataset.fb) return;
  e.currentTarget.dataset.fb = "1";
  e.currentTarget.src = IMG_FALLBACK;
};

const card = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: (i % 8) * 0.05, ease: [0.16, 1, 0.3, 1] } }),
};

export default function Collection() {
  useLenis();
  const { collection } = useParams();
  const navigate = useNavigate();
  const { t } = useContent();
  const [products, setProducts] = useState(null);
  const [sizeFilter, setSizeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("featured");
  const [visible, setVisible] = useState(12);
  const PAGE = 12;

  useEffect(() => { setVisible(PAGE); }, [collection, sizeFilter, statusFilter, sortBy]);

  const valid = VALID.includes(collection);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!valid) return;
    api.get(`/products?collection=${collection}`).then(({ data }) => {
      const sorted = [...data.products].sort((a, b) => (b.featured === true) - (a.featured === true));
      setProducts(sorted);
    }).catch(() => setProducts([]));
  }, [collection, valid]);

  if (!valid) {
    return (
      <div className="min-h-screen bg-[#090909] text-[#F2EFEB] flex flex-col items-center justify-center gap-6 font-mono-ui">
        <h1 className="font-display text-7xl">404 / NO SUCH COLLECTION</h1>
        <Link to="/" className="overline border-2 border-[#F2EFEB] px-6 py-3 hover:bg-[#DFFF00] hover:text-[#090909] transition-colors">← Back to Good Grief</Link>
      </div>
    );
  }

  return (
    <main data-testid="collection-page" className="w-full overflow-hidden bg-[#F2EFEB] text-[#090909] min-h-screen">
      <Nav />
      <div className="pt-16" />

      {/* Header */}
      <section className="px-4 md:px-8 pt-14 md:pt-20 pb-10 border-b-2 border-[#090909]">
        <button onClick={() => navigate("/")} data-testid="collection-back" className="overline text-[#FF2A00] hover:underline mb-6">← Home</button>
        <span className="overline block mb-3">{t(`col_${collection}_tagline`)}</span>
        <h1 className="font-display text-5xl sm:text-6xl md:text-8xl lg:text-[10rem] leading-[0.8] tracking-tight break-words">
          {t(`col_${collection}_title`)}
        </h1>
        <p className="font-serif-ed italic text-lg md:text-2xl mt-6 max-w-2xl">{t(`col_${collection}_desc`)}</p>
      </section>

      {/* Grid */}
      <section className="px-4 md:px-8 py-12 md:py-16">
        {products === null ? (
          <p className="font-mono-ui text-sm opacity-60 py-20 text-center animate-pulse">Loading inventory...</p>
        ) : products.length === 0 ? (
          <p data-testid="collection-empty" className="font-mono-ui text-sm opacity-60 py-20 text-center">Nothing here yet. Drop 01 incoming.</p>
        ) : (() => {
          const allSizes = [...new Set(products.flatMap((p) => p.sizes || []))];
          let filtered = products.filter((p) => {
            if (sizeFilter !== "all" && !(p.sizes || []).includes(sizeFilter)) return false;
            if (statusFilter === "available" && p.status !== "live") return false;
            if (statusFilter === "soon" && p.status === "live") return false;
            return true;
          });
          if (sortBy === "price-asc") filtered = [...filtered].sort((a, b) => a.price - b.price);
          else if (sortBy === "price-desc") filtered = [...filtered].sort((a, b) => b.price - a.price);
          else if (sortBy === "name") filtered = [...filtered].sort((a, b) => a.name.localeCompare(b.name));

          const selCls = "bg-transparent border-2 border-[#090909] px-3 h-10 font-mono-ui text-xs uppercase focus:outline-none focus:border-[#FF2A00] cursor-pointer";
          return (
            <>
              {/* Filter bar */}
              <div className="flex flex-wrap items-center gap-3 mb-8 pb-6 border-b-2 border-[#090909]" data-testid="collection-filters">
                <span className="overline text-[#FF2A00]">Filter</span>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="filter-status" className={selCls}>
                  <option value="all">All status</option>
                  <option value="available">Available</option>
                  <option value="soon">Coming soon</option>
                </select>
                <select value={sizeFilter} onChange={(e) => setSizeFilter(e.target.value)} data-testid="filter-size" className={selCls}>
                  <option value="all">All sizes</option>
                  {allSizes.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="filter-sort" className={selCls}>
                  <option value="featured">Featured</option>
                  <option value="price-asc">Price ↑</option>
                  <option value="price-desc">Price ↓</option>
                  <option value="name">A–Z</option>
                </select>
                {(sizeFilter !== "all" || statusFilter !== "all" || sortBy !== "featured") && (
                  <button onClick={() => { setSizeFilter("all"); setStatusFilter("all"); setSortBy("featured"); }} data-testid="filter-clear" className="font-mono-ui text-xs uppercase tracking-widest underline decoration-2 text-[#FF2A00] hover:opacity-70">Clear</button>
                )}
                <span className="overline opacity-60 ml-auto" data-testid="collection-count">{filtered.length} piece{filtered.length === 1 ? "" : "s"}</span>
              </div>

              {filtered.length === 0 ? (
                <p data-testid="filter-empty" className="font-mono-ui text-sm opacity-60 py-16 text-center">No pieces match those filters.</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                  {filtered.slice(0, visible).map((p, i) => (
                    <motion.button
                      key={p.slug}
                      custom={i}
                      variants={card}
                      initial="hidden"
                      whileInView="show"
                      viewport={{ once: true, margin: "-40px" }}
                      onClick={() => navigate(`/product/${p.slug}`)}
                      data-testid={`collection-product-${p.slug}`}
                      className="text-left border-2 border-[#090909] flex flex-col group relative bg-[#F2EFEB]"
                    >
                      <div className="relative overflow-hidden">
                        {p.featured && (
                          <span className="absolute top-2 left-2 z-10 font-mono-ui text-[0.6rem] uppercase px-2 py-0.5 border-2 border-[#090909] bg-[#FF2A00] text-[#F2EFEB] rotate-[3deg]">Pick</span>
                        )}
                        {p.status !== "live" && (
                          <span className="absolute top-2 right-2 z-10 font-mono-ui text-[0.6rem] uppercase px-2 py-0.5 border-2 border-[#090909] bg-[#DFFF00] text-[#090909] rotate-[-4deg]">Soon</span>
                        )}
                        {p.status === "live" && stockStatus(p).label && (
                          <span className={`absolute top-2 right-2 z-10 font-mono-ui text-[0.6rem] uppercase px-2 py-0.5 border-2 border-[#090909] rotate-[-4deg] ${stockStatus(p).tone === "out" ? "bg-[#FF2A00] text-[#F2EFEB]" : "bg-[#DFFF00] text-[#090909]"}`}>{stockStatus(p).label}</span>
                        )}
                        <img src={p.images?.[0] || IMG_FALLBACK} alt={p.name} onError={onImgError} className="block w-full aspect-square object-cover grayscale contrast-125 group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500" />
                      </div>
                      <div className="border-t-2 border-[#090909] p-3 flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-mono-ui text-xs uppercase leading-tight line-clamp-2">{p.name}</p>
                          <p className="font-mono-ui text-xs opacity-60 mt-0.5">${formatPrice(p.price)}</p>
                        </div>
                        <span className="font-display text-lg shrink-0 group-hover:translate-x-0.5 transition-transform">↗</span>
                      </div>
                    </motion.button>
                  ))}
                </div>
              )}
              {filtered.length > visible && (
                <div className="mt-10 flex justify-center">
                  <button
                    onClick={() => setVisible((v) => v + PAGE)}
                    data-testid="collection-load-more"
                    className="group inline-flex items-center gap-3 border-2 border-[#090909] px-8 py-4 font-mono-ui uppercase text-xs tracking-widest hover:bg-[#DFFF00] transition-colors"
                  >
                    Load more ({filtered.length - visible} left)
                    <span className="font-display text-lg group-hover:translate-y-0.5 transition-transform">↓</span>
                  </button>
                </div>
              )}
            </>
          );
        })()}
      </section>

      <Footer />
    </main>
  );
}
